import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import { Form, Formik, FormikHelpers } from "formik";
import { object, string } from "yup";
import { BranchFormValues } from "src/models/Branch.model";
import BranchForm from "../Layouts/BranchForm";
import {
  useEditBranchMutation,
  useViewBranchQuery,
} from "src/services/BranchServices";
import { showToast } from "src/utils/showToaster";
import { CircularProgress } from "@mui/material";

type Props = {
  onClose: () => void;
  branchId: string;
};

const EditBranchFormWrapper = ({ onClose, branchId }: Props) => {
  //STATE
  const [branchData, setBranchData] = useState<any>();

  //API
  const [editBranch] = useEditBranchMutation();

  const { data, isLoading, isFetching } = useViewBranchQuery(branchId);

  //USEffect
  useEffect(() => {
    if (!isLoading && !isFetching) {
      setBranchData(data?.data?.[0]);
    }
  }, [isFetching, isLoading, data]);

  // Form Initial Values
  const initialValues: BranchFormValues = {
    branchName: branchData?.branchName,
    branchCode: branchData?.branchCode,
    branchAddress: branchData?.branchAddress,
    country: {
      countryName: branchData?.countryLabel,
      _id: branchData?.countryId,
    },
    state: {
      stateName: branchData?.stateLabel,
      _id: branchData?.stateId,
    },
    city: {
      cityName: branchData?.cityLabel,
      _id: branchData?.cityId,
    },
    lat: branchData?.lat,
    long: branchData?.long,
  };

  // Validation Schema
  const validationSchema = object().shape({
    branchName: string().required("Branch name is required"),
    branchCode: string().required("Branch code is required"),
    branchAddress: string().required("Branch address is required"),
    country: object().required("Country is required"),
    state: object().required("State is required"),
    city: object().required("City is required"),
    lat: string().required("Latitude  is required"),
    long: string().required("Longitude  is required"),
  });

  // Handle Submit
  const handleSubmit = (
    values: BranchFormValues,
    { setSubmitting, resetForm }: FormikHelpers<BranchFormValues>
  ) => {
    const formattedValues = {
      branchName: values.branchName,
      branchCode: values.branchCode,
      branchAddress: values?.branchAddress,
      countryId: values?.country?._id,
      stateId: values?.state?._id,
      cityId: values?.city?._id,
      lat: values?.lat,
      long: values?.long,
    };

    editBranch({
      id: branchId,
      body: formattedValues,
    }).then((res: any) => {
      if (res?.error) {
        showToast("error", res?.error?.data?.message);
      } else {
        if (res?.data?.status) {
          showToast("success", res?.data?.message);
          resetForm();
          onClose();
        } else {
          showToast("error", res?.data?.message);
        }
      }
      setSubmitting(false);
    });
  };

  return (
    <Dialog open maxWidth="xs" fullWidth>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form>
            {(isLoading || isFetching) && (
              <div className="absolute w-[100%] h-[100%] flex justify-center items-center z-10 bg-slate-100 opacity-50">
                <CircularProgress />
              </div>
            )}
            <BranchForm
              formType="EDIT"
              formikProps={formikProps}
              onClose={onClose}
            />
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditBranchFormWrapper;
